'use client';

import { useTranslation } from 'next-i18next';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import {
  VideoCameraIcon,
  PhoneIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/solid';
import {
  Button,
  Dropdown,
  DropdownButton,
  DropdownItems,
  DropdownItem,
} from '@components/common';
import { cn } from '@root/src/utils/cn';
import { useDropdownHover } from '@hooks/common/useDropdownHover';
import useWindowDimensions from '@hooks/useMediaQuery';

interface InterviewResponseButtonProps {
  jobSlug: string;
  className?: string;
}

export const InterviewResponseButton = (props: InterviewResponseButtonProps) => {
  const { jobSlug, className } = props;
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();
  const { isTablet } = useWindowDimensions();

  // Enable hover behavior only on desktop
  const { dropdownRef, buttonRef, closeFuncRef } = useDropdownHover({ enabled: !isTablet });

  const handleInterviewTypeClick = (type: 'video' | 'audio' | 'text') => {
    switch (type) {
      case 'video':
        void router.push(`/job/${jobSlug}/ai-interview?type=video`);
        break;
      case 'audio':
        void router.push(`/job/${jobSlug}/ai-interview?type=audio`);
        break;
      case 'text':
        void router.push(`/job/${jobSlug}/ai-interview?type=text`);
        break;
      default:
        break;
    }
  };

  return (
    <Dropdown ref={dropdownRef} className="relative flex w-full">
      {({ close }) => {
        closeFuncRef.current = close;

        return (
          <>
            <DropdownButton
              as={Button}
              color="primary-light"
              rounding="full"
              styling="soft"
              size="xs"
              className={cn('group/dropdownButton flex flex-1 items-center justify-center gap-2 overflow-hidden !bg-primary/20 !text-xs !text-primary-light hover:!bg-primary-dark hover:!text-white', className)}
              ref={buttonRef}
            >
              <span>{t('job.card.ai-interview')}</span>
              <div className="flex items-center transition-colors duration-200 divide-x divide-primary-light group-hover/dropdownButton:divide-white">
                <div className="px-2">
                  <VideoCameraIcon className="size-4" />
                </div>
                <div className="px-2">
                  <PhoneIcon className="size-4" />
                </div>
                <div className="px-2">
                  <PencilSquareIcon className="size-4" />
                </div>
              </div>
            </DropdownButton>

            <DropdownItems
              animation="slideUp"
              className="flex flex-col w-64 gap-2 p-1 border rounded-md shadow-lg bottom-full border-surface-200 bg-surface"
            >
              {/* Video Interview Option */}
              <DropdownItem
                as={Button}
                type="button"
                styling="none"
                onClick={() => handleInterviewTypeClick('video')}
                className="flex items-center gap-3 px-2 py-1 text-sm text-left transition-colors rounded-md hover:bg-surface-100"
              >
                <VideoCameraIcon className="size-5" />
                <span className="font-medium text-ink">{t('ai.video.interview')}</span>
              </DropdownItem>

              {/* Audio Interview Option */}
              <DropdownItem
                as={Button}
                type="button"
                styling="none"
                onClick={() => handleInterviewTypeClick('audio')}
                className="flex items-center gap-3 px-2 py-1 text-sm text-left transition-colors rounded-md hover:bg-surface-100"
              >
                <PhoneIcon className="size-5" />
                <span className="font-medium text-ink">{t('ai.audio.interview')}</span>
              </DropdownItem>

              {/* Text Interview Option */}
              <DropdownItem
                as={Button}
                type="button"
                styling="none"
                onClick={() => handleInterviewTypeClick('text')}
                className="flex items-center gap-3 px-2 py-1 text-sm text-left transition-colors rounded-md hover:bg-surface-100"
              >
                <PencilSquareIcon className="size-5" />
                <span className="font-medium text-ink">{t('ai.text.interview')}</span>
              </DropdownItem>
            </DropdownItems>
          </>
        );
      }}
    </Dropdown>
  );
};
